import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useGeolocation } from '../../../hooks/useGeolocation';
import { useNearbyAdoptions } from '../../../hooks/useNearbyAdoptions';

export default function AdoptHero() {
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [manualZipCode, setManualZipCode] = useState('');
  const { location, loading: geoLoading, error: geoError, requestLocation } = useGeolocation();
  const { shelters, cats, loading: dataLoading } = useNearbyAdoptions(
    location?.latitude,
    location?.longitude
  );

  const handleChangeLocation = () => {
    setShowLocationModal(true);
  };

  const handleManualLocationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would geocode the zip code
    // For now, just close the modal
    setShowLocationModal(false);
  };

  const totalCats = cats?.length || 0;
  const totalShelters = shelters?.length || 0;

  return (
    <>
      <section className="relative h-[70vh] sm:h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/35c53e6ab26d7066ff61dde07b274399.jpg"
            alt="Adopt a cat from local shelters"
            title="Find adoptable cats near you"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-5 sm:px-6 max-w-4xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-6"
          >
            <div className="relative flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="font-body text-white text-sm font-medium">Live Data</span>
            </div>
            <span className="text-white/50">•</span>
            <i className="ri-heart-pulse-line text-accent text-lg"></i>
            <span className="font-body text-white text-sm font-medium">
              {totalShelters > 0 ? `${totalShelters} Local Shelters` : 'Partnered Shelters'}
            </span>
          </motion.div>

          <motion.h1
            className="font-heading font-bold text-3xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-4 sm:mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            FIND YOUR
            <br />
            <span className="text-accent">PURR-FECT</span> MATCH
          </motion.h1>

          <motion.p
            className="font-body text-sm sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            Hundreds of cats in local shelters are waiting for a loving home. Browse adoptable cats near you and change a life today.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-3 sm:gap-5 justify-center items-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a
              href="#adoptable-cats"
              className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-accent hover:bg-accent/90 text-white font-heading font-bold text-base sm:text-lg rounded-full transition-all duration-300 hover:scale-105 flex items-center justify-center gap-3 shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line text-xl"></i>
              Browse Cats Near Me
            </a>
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 font-heading font-bold text-base sm:text-lg rounded-full transition-all duration-300 hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-line text-xl"></i>
              Adoption FAQ
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <i className="ri-arrow-down-line text-3xl sm:text-4xl text-white/70"></i>
        </motion.div>
      </section>

      {/* Location Banner */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="bg-gradient-to-r from-accent/10 via-accent/5 to-accent/10 border-b border-accent/20"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-4 sm:py-5">
          {geoLoading ? (
            <div className="flex items-center justify-center gap-3">
              <div className="w-5 h-5 border-3 border-accent border-t-transparent rounded-full animate-spin"></div>
              <span className="font-body text-gray-700 text-sm sm:text-base">
                Detecting your location...
              </span>
            </div>
          ) : geoError || !location ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <i className="ri-map-pin-line text-amber-600 text-xl"></i>
                </div>
                <div className="text-center sm:text-left">
                  <p className="font-heading font-bold text-gray-900 text-sm sm:text-base">
                    Enable Location for Better Results
                  </p>
                  <p className="font-body text-gray-600 text-xs sm:text-sm">
                    We'll show you cats available in your area
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={requestLocation}
                  className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white font-heading font-bold text-sm rounded-full transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-map-pin-user-line text-base"></i>
                  Enable Location
                </button>
                <button
                  onClick={handleChangeLocation}
                  className="px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-heading font-bold text-sm rounded-full transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line text-base"></i>
                  Enter Zip Code
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-start">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-fill text-accent text-xl"></i>
                  <span className="font-heading font-bold text-gray-900 text-sm sm:text-base">
                    Showing cats near {location.city}, {location.state}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-gray-200">
                    <i className="ri-home-heart-line text-accent"></i>
                    <span className="font-body text-gray-700">
                      <strong className="font-bold text-gray-900">{totalShelters}</strong> Shelters
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-gray-200">
                    <i className="ri-heart-3-line text-rose-500"></i>
                    <span className="font-body text-gray-700">
                      <strong className="font-bold text-gray-900">{totalCats}</strong> Cats Available
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-gray-200">
                    <i className="ri-trophy-line text-amber-500"></i>
                    <span className="font-body text-gray-700">
                      <strong className="font-bold text-gray-900">2,847</strong> Happy Adoptions
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleChangeLocation}
                className="px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-heading font-bold text-sm rounded-full transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-map-pin-line text-base"></i>
                Change Location
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Change Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-gray-900">
                Change Location
              </h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>

            <form onSubmit={handleManualLocationSubmit} className="space-y-4">
              <div>
                <label className="block font-body font-medium text-gray-700 text-sm mb-2">
                  Enter Zip Code or City
                </label>
                <input
                  type="text"
                  value={manualZipCode}
                  onChange={(e) => setManualZipCode(e.target.value)}
                  placeholder="e.g., 90210 or Los Angeles, CA"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg font-body text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-accent hover:bg-accent/90 text-white font-heading font-bold text-sm rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Update Location
                </button>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-heading font-bold text-sm rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowLocationModal(false);
                  requestLocation();
                }}
                className="w-full px-6 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 font-body text-sm rounded-lg transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-map-pin-user-line text-base"></i>
                Or use my current location
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}